import React, { useEffect, useState } from "react";
import { array, bool, func, object, string } from "prop-types";
import { get } from "lodash";
import { ResponsiveImage } from "@quintype/components";
import { useSelector } from "react-redux";
import ExclamatinoLogo from "../../atoms/ExclamationLogo";
import { SvgIconHandler } from "../../atoms";
import SummaryCard from "../../molecules/SummaryCard";
import { useDeviceType } from "../../customHooks/useDeviceType";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { STATIC_KEYWORDS } from "../../../constants";
import { getTemplateName } from "../../../ga-utils";

import "./summary-page.m.css";
import { isMobile } from "react-device-detect";

const getSectionName = (section = "") => {
  if (typeof section === "string") {
    return section;
  }

  return get(section, ["name"], "");
};

const getSectionSlug = (section = "") => {
  if (typeof section === "string") {
    return "";
  }

  return get(section, ["slug"], "");
};

const slugifySection = (section = "") =>
  getSectionName(section)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const dedupeStories = (stories = []) => {
  const seen = new Set();

  return stories.filter((story) => {
    const storyKey = story?.articleId || story?.id || story?.slug;
    if (!storyKey || seen.has(storyKey)) {
      return false;
    }

    seen.add(storyKey);
    return true;
  });
};

const checkSummary = (cards) => {
  if (!Array.isArray(cards) || cards.length === 0) {
    return false;
  }

  const lastCardElements = get(cards[cards.length - 1], ["story-elements"], []);
  const lastElement = lastCardElements[lastCardElements.length - 1];
  const secondLastElement = lastCardElements[lastCardElements.length - 2];

  return (
    lastElement?.subtype === "summary" ||
    (lastElement?.metadata?.["promotional-message"] === true && secondLastElement?.subtype === "summary")
  );
};

const getQtSummaryPageData = (collection = {}) => {
  const collectionItems = get(collection, ["items"], []);
  const allStories = collectionItems
    .filter((item) => item?.type === "story" && checkSummary(item?.story?.cards))
    .map((item) => item.story);

  const menuGroup = collectionItems
    .filter((item) => item?.type === "collection")
    .map((item) => {
      const stories = get(item, ["items"], [])
        .filter((collectionItem) => collectionItem?.type === "story" && checkSummary(collectionItem?.story?.cards))
        .map((collectionItem) => collectionItem.story);

      return { items: stories, slug: item?.slug, name: item?.name };
    });

  return { allStories, menuGroup };
};

const isAbsoluteImageUrl = (image = "") => /^https?:\/\//i.test(image) || image.startsWith("//");

const normalizeSummaryImageUrl = (image = "") => {
  if (!image || typeof image !== "string") return "";

  const mediaUrlIndex = image.indexOf("https://media.openthemagazine.com/");
  if (mediaUrlIndex >= 0) {
    return image.slice(mediaUrlIndex);
  }

  return image.startsWith("//") ? `https:${image}` : image;
};

const getSummaryImageSlug = (story = {}) =>
  story.story ? story?.story["hero-image-s3-key"] : story["hero-image-s3-key"];

const ImageStack = ({ stories }) => {
  return (
    <div styleName="image-stack">
      {stories?.map((story, index) => {
        const imageSlug = getSummaryImageSlug(story);
        const imageUrl = normalizeSummaryImageUrl(imageSlug);

        return (
          <div
            key={index}
            styleName="image-wrapper"
            style={{
              top: `${index * 3}px`,
              left: `${index * 4}px`,
            }}
          >
            {isAbsoluteImageUrl(imageSlug) ? (
              <img className="qt-image" src={imageUrl} alt={story?.headline || ""} loading="lazy" decoding="async" />
            ) : (
              <ResponsiveImage
                slug={imageSlug}
                aspectRatio={[16, 9]}
                defaultWidth={62}
                imgParams={{ auto: ["format", "compress"], q: 70 }}
                shouldDecodeImage={true}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

ImageStack.propTypes = {
  stories: array,
};
const SummaryMenu = ({ menuGroup, handleCategoryClick, activeCategory, isDesktop, summarySponsor }) => {
  return (
    <div styleName="summary-menu">
      <div styleName="top-header">
        <button
          styleName="close-btn"
          onClick={() => {
            const directVisit = document.referrer === "" || window.history.length <= 1;

            if (directVisit) {
              window.location.href = "/";
            } else {
              window.history.back();
            }
          }}
        >
          <SvgIconHandler
            type={isDesktop ? "left-arrow" : "close"}
            width={isDesktop ? "16" : "12"}
            height={isDesktop ? "16" : "12"}
          />
        </button>
        <p styleName="welcome">{STATIC_KEYWORDS.WELCOME}</p>
        <h1 styleName="title">{STATIC_KEYWORDS.OPEN_SUMMARIES}</h1>
        <p styleName="subtitle">{STATIC_KEYWORDS.SUMMARY_SUBTITLE}</p>
        {isDesktop && <><div styleName="summary-icon-wrapper">
          <span styleName="summary-icon">
            <SvgIconHandler type={"summary"} width="18" height="18" />
          </span>
        </div>
        <h2 styleName="categories-text">{STATIC_KEYWORDS.SELECT_CATEGORY}</h2>
        </>
}
      </div>
      <div styleName="categories">
        {menuGroup?.map((menu, index) => {
          return menu?.items?.length ? (
            <div key={index} styleName="category-card" onClick={() => handleCategoryClick(menu?.slug)}>
              <div styleName="left-content">
                <p styleName={`name ${activeCategory === menu.slug ? "active" : ""}`}>{menu?.name}</p>
                <p styleName="number">{menu?.items?.length} Stories</p>
              </div>
              {!isDesktop && (
                <div styleName="right-content">
                  <ImageStack stories={menu?.items?.slice(0, 6)} />
                </div>
              )}
            </div>
          ) : null;
        })}
      </div>
      {summarySponsor ? (
        <div styleName="bottom-sponsor-text">
          <span>{STATIC_KEYWORDS.POWERED_BY}</span>
          {" X"}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

SummaryMenu.propTypes = {
  menuGroup: array,
  handleCategoryClick: func,
  activeCategory: string,
  isDesktop: bool,
  summarySponsor: bool,
};

const SummaryPage = (props) => {
  const {AI_WARNING_NEW } = STATIC_KEYWORDS;
  const shortNews = props?.data?.shortNews;
  const selectedStory = shortNews?.selected || null;
  const shortNewsItems = shortNews?.items || [];
  const apiStories = dedupeStories([selectedStory, ...shortNewsItems].filter(Boolean));
  const sectionGroupsMap = apiStories.reduce((acc, story) => {
    const sectionName = getSectionName(story?.section) || story?.section || "Other";
    const sectionSlug =
      story?.["section-slug"] || getSectionSlug(story?.section) || slugifySection(sectionName) || "other";

    if (!acc[sectionSlug]) {
      acc[sectionSlug] = { items: [], slug: sectionSlug, name: sectionName };
    }

    acc[sectionSlug].items.push(story);
    return acc;
  }, {});
  const apiMenuGroup = Object.values(sectionGroupsMap);
  const qtSummaryData = getQtSummaryPageData(props?.data?.collection);
  const allStories = shortNews ? apiStories : qtSummaryData.allStories;
  const menuGroup = shortNews ? apiMenuGroup : qtSummaryData.menuGroup;
  const updatedGroup = [{ items: allStories, slug: "all-stories", name: "All Stories" }, ...menuGroup];

  const { summary_sponsor: summarySponsor } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes"], false)
  );

  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));
  const [showSummary, setShowSummary] = useState(false);
  const [collectionStories, setCollectionStories] = useState(allStories);
  const [activeCategory, setActiveCategory] = useState("all-stories");
  const isDesktop = useDeviceType() === "desktop";
  const [showMoreContent, setShowMoreContent] = useState(false);

  // Track category clicks for the SummaryMenu component
  const trackCategoryClick = (slug) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "short_news_category_click",
        link_text: slug,
        category: "short-news",
        sub_category: "NA",
        content_id: "NA",
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleCategoryClick = (slug) => {
    // Track the category click event
    trackCategoryClick(slug);

    categoryLandingDL(slug);
    if (slug === "all-stories") {
      setCollectionStories(allStories);
    } else {
      const collectionGroup = updatedGroup.find((item, index) => item.slug === slug);
      const collectionStories = collectionGroup?.items || [];
      setCollectionStories(collectionStories);
    }
    setShowSummary(true);
    setActiveCategory(slug);
  };

  const categoryLandingDL = (categoryName) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "short-news",
      sub_category: categoryName,
      content_id: "NA",
      template_name: "listing",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in",
      subscription_status: "not-subscribed",
      ad_free: false,
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  };

  useEffect(() => {
    setCollectionStories(allStories);
  }, [allStories.length]);

  useEffect(() => {
    categoryLandingDL("NA");
    isDesktop && categoryLandingDL("all-stories");
  }, []);
  const isAdfree = props?.config?.subscriber === "true";
  const summaryWrapperStyle = isAdfree ? "summary-wrapper-no-ad" : "ad-comp";

  // Auto-scroll to summary story via slug
  useEffect(() => {
    if (allStories.length === 0) return;
    const params = window && new URLSearchParams(window.location.search);
    const slugFromUrl = params?.get("slug");
    if (slugFromUrl) {
      const pathParts = slugFromUrl.split("/");
      const publicIdFromUrl = pathParts[pathParts.length - 1];
      const selectedSummaryStory = allStories.find(
        (story) => story?.slug === slugFromUrl || story?.["public-identifier"] === publicIdFromUrl
      );
      const matchingCategory = menuGroup.find((group) =>
        group?.items?.some(
          (story) => story?.slug === selectedSummaryStory?.slug || story?.id === selectedSummaryStory?.id
        )
      );

      if (matchingCategory) {
        setCollectionStories(matchingCategory.items);
        setActiveCategory(matchingCategory.slug);
      } else {
        setCollectionStories(allStories);
        setActiveCategory("all-stories");
      }
      setShowSummary(true);
    }
  }, [allStories.length]);

  const handleReadmore = () => {
    setShowMoreContent(true);
  }
  const handleReadmoreClose = () => {
    setShowMoreContent(false);
  }

  return (
    <>
    <div styleName="summary-page-container">
      {(!showSummary || isDesktop) && (
        <>
        <SummaryMenu
          menuGroup={updatedGroup}
          handleCategoryClick={handleCategoryClick}
          activeCategory={activeCategory}
          isDesktop={isDesktop}
          summarySponsor={summarySponsor}
        />
        {!isDesktop &&
        <div styleName="ai-warning">
          <div styleName="summary-disclaimer-readmore">
            <ExclamatinoLogo width="12" height="12" viewBox="0 0 14 14" />
          {AI_WARNING_NEW}
          {!showMoreContent && <button aria-label="read more" onClick={() => handleReadmore()} styleName="disclaimer-read-more">...read more</button>}
          </div>
        </div>
        }
                            </>
      )}
      {(showSummary || isDesktop) && collectionStories?.length > 0 && (
        <SummaryCard
          setShowSummary={setShowSummary}
          menuGroup={menuGroup}
          story={collectionStories[0]}
          collectionStories={collectionStories.slice(1, collectionStories.length)}
          pageType="summary-page"
        />
      )}
      <div styleName={summaryWrapperStyle}>
        {!isAdfree && (
          <ListingAdSlot
            adStyleName="ad-slot-size-300x250"
            adType="right_side"
            pageType="summary-page"
            adSlotConfigName="mRec_ad"
          />
        )}
      </div>
      <>
        <div styleName={`${showMoreContent ? 'open' : ''} readmore-popup-content`}>
      <button aria-label="read more close" onClick={() => handleReadmoreClose()} styleName="read-more-close"></button>
      {AI_WARNING_NEW}</div></>
    </div>
      </>
  );
};

SummaryPage.propTypes = {
  config: object,
  data: object,
};

export { SummaryPage };
